// Lab Content Selector — interactive front-end for /api/lab/content/preview.
//
// Lets operators build a ContentSelectionConfig visually and see exactly
// which articles (and how many) it would surface for a campaign — with
// optional per-subscriber suppression preview when sample subscriber IDs
// are provided.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { api } from '../api/client'
import {
  ContentSelectionConfigForm,
  DEFAULT_CONTENT_SELECTION_CONFIG,
  type ContentSelectionConfig,
} from './ContentSelectionConfigForm'

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

type ArticleOut = {
  id: number
  publication_slug: string
  site_type: string
  title: string
  slug: string | null
  wp_url: string
  wp_post_id: number
  post_date: string | null
  featured_image_url: string | null
  story_type: string | null
  era: string | null
  ai_tags: string[] | null
  short_teaser: string | null
  long_teaser: string | null
  ai_confidence: number | null
  ai_model: string | null
  editor_priority: number
  engagement_score: number
  enriched: boolean
  embedded: boolean
}

type PreviewIn = {
  config: ContentSelectionConfig
  sample_subscriber_ids: number[] | null
}

type PreviewOut = {
  matches: ArticleOut[]
  by_subscriber: Record<string, number[]> | null
}

export function LabContentSelectorPage() {
  const [config, setConfig] = useState<ContentSelectionConfig>(DEFAULT_CONTENT_SELECTION_CONFIG)
  const [sampleIdsRaw, setSampleIdsRaw] = useState<string>('')
  const [debouncedConfig, setDebouncedConfig] = useState(config)
  const [debouncedSampleIds, setDebouncedSampleIds] = useState<number[] | null>(null)

  // Debounce so typing in tag inputs doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedConfig(config), 350)
    return () => clearTimeout(t)
  }, [config])

  useEffect(() => {
    const t = setTimeout(() => {
      const ids = sampleIdsRaw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n) && n > 0)
      setDebouncedSampleIds(ids.length > 0 ? ids : null)
    }, 350)
    return () => clearTimeout(t)
  }, [sampleIdsRaw])

  const preview = useQuery({
    queryKey: ['lab', 'content', 'preview', debouncedConfig, debouncedSampleIds],
    queryFn: () =>
      api.post<PreviewOut>('/api/lab/content/preview', {
        config: debouncedConfig,
        sample_subscriber_ids: debouncedSampleIds,
      } satisfies PreviewIn),
    retry: false,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1500px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label">LAB · L2</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper mt-1">
          Content Selector
        </h1>
        <p className="text-paper-trace text-[13px] mt-2 font-serif max-w-[820px]">
          Pick articles for an email template's content slots. Filters mirror
          <span className="font-mono text-paper"> lab_email_templates.content_selection_config</span>;
          the live preview shows exactly what the dispatcher would render.
          Add sample subscriber IDs to see per-member suppression.
        </p>
      </header>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5 space-y-6">
          <ContentSelectionConfigForm config={config} onChange={setConfig} />
          <Panel label="Per-subscriber preview" accent="cyan">
            <label className="block">
              <span className="label mb-1.5 block">Sample subscriber IDs</span>
              <textarea
                value={sampleIdsRaw}
                onChange={(e) => setSampleIdsRaw(e.target.value)}
                placeholder="12345, 12346, 12347"
                rows={2}
                className="w-full bg-ink-700 border border-ink-500 px-3 py-2 font-mono text-[12px] text-paper resize-y"
              />
              <span className="font-mono text-[11px] text-paper-trace mt-1.5 block">
                Comma- or whitespace-separated subscriber IDs. With IDs:
                each member's last {config.suppression_window_days}d of
                impressions are suppressed; results are grouped per member.
              </span>
            </label>
          </Panel>
        </div>

        <div className="col-span-12 lg:col-span-7 space-y-3">
          <PreviewPanel
            data={preview.data}
            loading={preview.isLoading}
            isSampleMode={Array.isArray(debouncedSampleIds) && debouncedSampleIds.length > 0}
          />
        </div>
      </div>
    </div>
  )
}

// ---------- Preview ----------

function PreviewPanel({
  data, loading, isSampleMode,
}: {
  data: PreviewOut | undefined
  loading: boolean
  isSampleMode: boolean
}) {
  if (loading && !data) {
    return (
      <Panel label="Preview" accent="green">
        <div className="label text-paper-trace animate-pulse py-8 text-center">LOADING</div>
      </Panel>
    )
  }
  if (!data) {
    return (
      <Panel label="Preview" accent="green">
        <div className="label text-paper-trace py-8 text-center">No preview yet</div>
      </Panel>
    )
  }

  if (isSampleMode && data.by_subscriber) {
    const articleById = new Map(data.matches.map((a) => [a.id, a]))
    const subEntries = Object.entries(data.by_subscriber)
    return (
      <Panel
        label={`Per-subscriber preview · ${subEntries.length} member${subEntries.length === 1 ? '' : 's'}`}
        accent="green"
        bodyClassName="p-0"
      >
        <div className="divide-y divide-ink-500">
          {subEntries.map(([sid, articleIds]) => (
            <div key={sid} className="p-3">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-mono text-[11px] tracking-label text-amber-glow">
                  SUB {sid}
                </span>
                <span className="label text-paper-trace">
                  {articleIds.length} article{articleIds.length === 1 ? '' : 's'} after suppression
                </span>
              </div>
              {articleIds.length === 0 ? (
                <div className="label text-paper-trace py-2">
                  No matches for this subscriber — every candidate was suppressed
                  or none exist in the configured filter.
                </div>
              ) : (
                <div className="space-y-2">
                  {articleIds.map((id) => {
                    const a = articleById.get(id)
                    if (!a) return null
                    return <ArticleCard key={id} article={a} />
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>
    )
  }

  return (
    <Panel
      label={`Matches · ${data.matches.length}`}
      accent="green"
      bodyClassName="p-0"
    >
      {data.matches.length === 0 ? (
        <div className="label text-paper-trace py-8 text-center">
          No articles match these filters
        </div>
      ) : (
        <div className="divide-y divide-ink-500">
          {data.matches.map((a) => (
            <div key={a.id} className="p-3">
              <ArticleCard article={a} />
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}

function ArticleCard({ article: a }: { article: ArticleOut }) {
  return (
    <div className="flex gap-3">
      {a.featured_image_url ? (
        <img
          src={a.featured_image_url}
          alt=""
          className="w-24 h-16 object-cover border border-paper-trace flex-shrink-0"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }}
        />
      ) : (
        <div className="w-24 h-16 border border-paper-trace bg-paper-deepest flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <Link
          to={`/lab/archive/${a.id}`}
          className="font-serif text-paper text-[14px] leading-tight hover:text-amber-glow"
        >
          {a.title}
        </Link>
        <div className="mt-0.5 flex flex-wrap gap-2 items-center">
          <span className="label text-paper-trace">{PUB_NAMES[a.publication_slug] ?? a.publication_slug}</span>
          {a.story_type && (
            <span className="font-mono text-[10px] tracking-label text-radar-cyan uppercase">
              {a.story_type}
            </span>
          )}
          {a.era && (
            <span className="font-mono text-[10px] tracking-label text-paper-trace">
              · {a.era}
            </span>
          )}
          <span className="font-mono text-[10px] text-paper-faint ml-auto tabular-nums">
            score {a.engagement_score.toFixed(0)}
          </span>
        </div>
        {a.short_teaser && (
          <div className="mt-1 font-serif text-[12px] text-paper-trace leading-snug line-clamp-2">
            {a.short_teaser}
          </div>
        )}
      </div>
    </div>
  )
}

